import { getToken, refreshAccessToken, redirectToLogin } from './AuthProvider';

/**
 * Thrown when a request exceeds its `timeout`. Re-exported by the app api-clients
 * so existing `instanceof TimeoutError` checks keep working.
 */
export class TimeoutError extends Error {
  /** Optional user-facing message callers attach for snackbar/UI display. */
  public userMessage?: string;

  constructor(message = 'Request timed out', userMessage?: string) {
    super(message);
    this.name = 'TimeoutError';
    this.userMessage = userMessage;
  }
}

export interface AuthedFetchOptions {
  /**
   * Skip the Authorization header entirely. Use for requests that must NOT
   * carry our bearer: S3 presigned URLs (signed via query params) and any
   * external/3rd-party host.
   */
  skipAuth?: boolean;
  /** Abort the request after this many ms (rejects with TimeoutError). */
  timeout?: number;
  /** Caller-supplied abort signal, merged with the timeout signal if both exist. */
  signal?: AbortSignal;
  /**
   * Rebuild the RequestInit for each attempt. Required when the body is a
   * one-shot value (Blob / FormData / ReadableStream) so the 401 retry can
   * resend it. When omitted, `init` is reused (safe for string / JSON bodies).
   */
  makeInit?: () => RequestInit;
}

// Module-level guard so a burst of unrecoverable 401s triggers exactly one
// visible sign-in redirect (the page is navigating away).
let redirectingToLogin = false;

function withAuth(init: RequestInit, token: string): RequestInit {
  if (!token) return init;
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

async function runFetch(
  url: string,
  init: RequestInit,
  timeout: number | undefined,
  externalSignal: AbortSignal | undefined,
): Promise<Response> {
  if (!timeout) {
    return fetch(url, externalSignal ? { ...init, signal: externalSignal } : init);
  }

  const timeoutController = new AbortController();
  const timer = setTimeout(() => timeoutController.abort(), timeout);
  const signal = externalSignal
    ? AbortSignal.any([timeoutController.signal, externalSignal])
    : timeoutController.signal;

  try {
    return await fetch(url, { ...init, signal });
  } catch (err) {
    if ((err as Error).name === 'AbortError' && timeoutController.signal.aborted) {
      throw new TimeoutError(`Request timed out after ${timeout}ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * fetch() to one of our MVC/backends with the CAD/OIDC bearer attached, plus a
 * single reactive refresh-and-retry on 401.
 *
 * - Injects `Authorization: Bearer <getToken()>` unless `skipAuth` is set or the
 *   token is empty.
 * - On a 401: calls `refreshAccessToken()` (oidc-client-ts `signinSilent`); if it
 *   succeeds, retries the request EXACTLY ONCE with the fresh token.
 * - If refresh fails (or the retry still 401s): sends the user to a VISIBLE
 *   sign-in via `redirectToLogin()` — never a silent failure — guarded so a burst
 *   of concurrent 401s redirects only once. The 401 Response is still returned so
 *   the caller can map it to its own error.
 *
 * Only use this for our backends. S3 presigned URLs / external hosts must pass
 * `{ skipAuth: true }` (or use plain `fetch`).
 */
export async function authedFetch(
  url: string,
  init: RequestInit = {},
  opts: AuthedFetchOptions = {},
): Promise<Response> {
  const { skipAuth = false, timeout, signal, makeInit } = opts;
  const build = makeInit ?? (() => init);

  if (skipAuth) {
    return runFetch(url, build(), timeout, signal);
  }

  const token = await getToken();
  let response = await runFetch(url, withAuth(build(), token), timeout, signal);
  if (response.status !== 401) return response;

  // Reactive refresh: one silent attempt, then one retry with the fresh token.
  const refreshed = await refreshAccessToken();
  if (refreshed) {
    const freshToken = await getToken();
    response = await runFetch(url, withAuth(build(), freshToken), timeout, signal);
    if (response.status !== 401) return response;
  }

  // Unrecoverable 401 — send the user to a visible sign-in, exactly once.
  if (!redirectingToLogin) {
    redirectingToLogin = true;
    void redirectToLogin();
  }
  return response;
}
